import { useEffect } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import type { WinningTopicState } from "@/state/store";

/**
 * Resolves the winning topic (most votes; ties broken by `topic.order`).
 *
 * Reads from the central store — actual polling happens in
 * `useWinningTopicPolling()`, which is mounted once at app-Shell level.
 * That collapses N components × M instances into a single fetch loop,
 * which matters when 64 phones poll simultaneously.
 *
 * Returns null until both topics + tallies are loaded, OR when all
 * votes have been reset (so the UI can clear stale winners).
 */
export function useWinningTopic(): WinningTopicState | null {
  return useStore().state.workshop.winningTopic;
}

/**
 * Single, app-level polling loop. Mount exactly ONCE (Shell). 10s cadence
 * — the winner barely flips during the working phase, but a reset must
 * be detected within a couple of cycles.
 */
export function useWinningTopicPolling(intervalMs = 10000) {
  const { state, dispatch } = useStore();
  const token = state.auth.token;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        // GET /winner ist für alle Rollen offen (Tallies bleiben
        // presenter-only) und respektiert einen eingefrorenen Sieger —
        // wichtig nach der Auslosung bei Gleichstand: die alte
        // Tally-Berechnung hätte dort still das falsche Thema angezeigt.
        const [t, w] = await Promise.all([
          api.topics.list(token),
          api.winner.get(token),
        ]);
        if (cancelled) return;

        if (!w.winner) {
          // Reset-bug fix: kein Sieger → leeren, keinen alten stehen lassen.
          dispatch({ type: "winningTopic/set", value: null });
          return;
        }

        const topic =
          t.topics.find((x) => x.id === w.winner!.topicId) ??
          // Thema evtl. gerade gelöscht/archiviert — Titel reicht fürs Banner.
          ({ id: w.winner.topicId, title: w.winner.topicTitle, order: 0, createdAt: 0 } as (typeof t.topics)[number]);
        dispatch({ type: "winningTopic/set", value: { topic, count: 0 } });
      } catch {
        /* swallow transient errors */
      }
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, intervalMs, dispatch]);
}
